import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  page: {
    path: '/workspace-learning-content',
    pageLabel: '기본 학습내용 등록',
    openSelectors: [
      '#cp-sidebar-class-common-trigger',
      'a[href^="/workspace-learning-content"]',
    ],
    openLabel: '기본 학습내용 등록',
    accessLabel: '사이드바 「학습내용, 평가계획」',
  },
  addModal: {
    path: '/workspace-learning-content',
    openSelector: '[data-tutorial-lc="add-template"], [aria-label="사용자 편제 추가하기"]',
    pageLabel: '기본 학습내용 등록',
    openLabel: '사용자 편제 추가하기',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-learning-content': R.page,
  overview: R.page,
  'select-grade': R.page,
  'select-term': R.page,
  'legend-guide': R.page,
  'template-tabs-guide': R.page,
  'open-add-template': R.page,
  'add-template-modal': R.addModal,
  'table-guide': R.page,
  'save-all': R.page,
};

const learningContentRaw: TutorialDefinition = {
  id: 'learning-content',
  title: '기본 학습내용 등록',
  summary: '학년·학기를 고르고 기본·팀장·사용자 편제를 이해한 뒤, 사용자 편제를 추가하는 방법을 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '기본 학습내용 등록 튜토리얼을 시작해요! 학년·학기를 고르고, 기본·팀장·사용자 편제의 차이를 이해한 뒤 내 편제를 추가해 볼게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '기본 학습내용 등록을 시작해요!',
      placement: 'center',
    },
    {
      id: 'go-learning-content',
      mascot:
        '사이드바에서 「학습내용, 평가계획」을 펼친 뒤 "기본 학습내용 등록"을 눌러 이동해 주세요.',
      targetSelector:
        '#cp-sidebar-class-common-trigger, a[href^="/workspace-learning-content"]',
      placement: 'right',
      advanceOnPathPrefix: '/workspace-learning-content',
      section: '[학습내용] 페이지 열기',
      sectionMascot: '기본 학습내용 등록 화면으로 이동하는 구간이에요.',
    },
    {
      id: 'overview',
      mascot:
        '이 화면에서는 관리자·팀장이 만든 학습내용을 읽고, 내 워크스페이스용 사용자 편제를 만들어 편집할 수 있어요. 전체 구성을 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-lc="page"]',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[학습내용] 화면 둘러보기',
      sectionMascot: '화면 구성과 편제 개념을 알아보는 구간이에요.',
    },
    {
      id: 'select-grade',
      mascot:
        '상단 숫자 탭이 학년이에요. 원하는 학년(예: 3)을 눌러 선택한 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-lc="grade-tabs"], [aria-label="학년 카테고리"]',
      manualAdvance: true,
      section: '[학습내용] 학년·학기 선택',
      sectionMascot: '학년과 학기를 고르는 구간이에요.',
    },
    {
      id: 'select-term',
      mascot:
        '학년 옆 탭으로 1학기·2학기·학기 구분 없음을 고를 수 있어요. 하나를 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-lc="term-tabs"], [aria-label="하위 카테고리"]',
      manualAdvance: true,
    },
    {
      id: 'legend-guide',
      mascot:
        '점 색이 편제 출처예요. [[dot:default]] 관리자 기본(읽기 전용), [[dot:team]] 팀장 편제(읽기 전용), [[dot:user]] 내 사용자 편제(편집·저장 가능)입니다. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-lc="legend"], [aria-label="편제 종류 범례"]',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[학습내용] 편제 종류',
      sectionMascot: '기본·팀장·사용자 편제를 구분하는 구간이에요.',
    },
    {
      id: 'template-tabs-guide',
      mascot:
        '아래 편제 탭에서 기본·팀장·사용자 편제를 골라 표를 볼 수 있어요. 이름이 같아도 출처가 다르면 서로 다른 데이터예요. 탭을 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-lc="template-tabs"], [aria-label="편제 목록"]',
      manualAdvance: true,
    },
    {
      id: 'open-add-template',
      mascot: '"사용자 편제 추가하기 +"를 눌러 새 편제 만들기 창을 열어 주세요.',
      targetSelector: '[data-tutorial-lc="add-template"], [aria-label="사용자 편제 추가하기"]',
      advanceWhenSelectorExists:
        '[data-tutorial-lc="add-template-modal"], .cp-admin-learning-template-modal',
      section: '[학습내용] 사용자 편제 추가',
      sectionMascot: '내 편제를 만들고 표를 살펴보는 구간이에요.',
    },
    {
      id: 'add-template-modal',
      mascot:
        '모달에서 학기 위치와 편제 이름을 입력한 뒤 "확인"을 누르면 편제가 만들어지고 다음으로 이어져요. 건너뛰려면 "다음"을 눌러 주세요(모달이 닫힙니다).',
      targetSelector:
        '[data-tutorial-lc="add-template-modal"], .cp-admin-learning-template-modal',
      advanceWhenSelectorGone:
        '[data-tutorial-lc="add-template-modal"], .cp-admin-learning-template-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'table-guide',
      mascot:
        '[[dot:user]] 사용자 편제를 선택하면 표에서 단원·학습내용·차시 등을 편집할 수 있어요. [[dot:default]] 기본·[[dot:team]] 팀장 편제는 읽기만 됩니다. 표를 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-lc="table"], .cp-admin-learning-table-grow',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '사용자 편제를 선택한 뒤 표가 나타나요. 편제 탭에서 [[dot:user]] 사용자 편제를 고르거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'save-all',
      mascot:
        '편집한 내용은 오른쪽 위 "저장하기"로 남겨 주세요. 변경이 없으면 버튼이 비활성일 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-lc="save"], .cp-admin-learning-page .cp-btn-save-all',
      manualAdvance: true,
      section: '[학습내용] 저장',
      sectionMascot: '작업한 내용을 저장하는 구간이에요.',
    },
    {
      id: 'outro',
      mascot:
        '기본 학습내용 등록 튜토리얼을 마쳤어요! 등록한 내용은 편제별 프로그램의 「학습내용 가져오기」에서 불러와 쓸 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const learningContent: TutorialDefinition = {
  ...learningContentRaw,
  steps: learningContentRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint: buildTutorialTargetMissingHint(recover, step.targetMissingHint),
    };
  }),
};

export default learningContent;
